import React, { FC, ReactNode, createContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getTaskList } from "@/api/ai-task/action";
import { FetchRawResponse } from "@/api/fetchRaw";
// import { handleRefreshToken } from "@/api/fetchRaw";
import { followingPageChat, newPageChat } from "@/api/message/actions";
import { TaskStatusType, followingChatResponse, sessionmgrTaskResponse } from "@/api/message/type";
import { canonicalizeSessionmgrPublicPath, joinServiceUrl, sessionmgrPublicPath } from "@/api/servicePaths";
import { getManagementTenantInfo } from "@/api/tenants/actions";
import type { AssistantProfileSelection } from "@/components/features/assistant-profile/AssistantProfileDisclosure";
import {
  autoAssistantProfileSelection,
  findAssistantProfileDisclosure,
  parseAssistantProfileDisclosure,
  rememberAssistantProfileSelection as persistRememberedAssistantProfileSelection,
  readRememberedAssistantProfileSelection,
  toAssistantProfileSelectionHint
} from "@/components/features/assistant-profile/assistantProfileChatAdapter";
import type { AssistantProfileDisclosureModel } from "@/components/features/assistant-profile/assistantProfileChatAdapter";
import { CodeModalProps } from "@/components/tasks/CodeModal";
import { SESSIONMGR_URL } from "@/configs";
import { resolveAssistantPageSlugByPath } from "@/configs/router/routerConfig";
import { useDashboard } from "@/contexts/DashboardContext";
import { ensureSuperTokensInitialized } from "@/lib/auth/supertokensInit";
import { logger } from "@/lib/logger";
import { isSignupSetupRoute } from "@/lib/signup/setupRouting";
import { MessageModel } from "@/models/message";
import { AIState, pushCopiedText, pushPageId, pushParams } from "@/slices/AI-Infor/AIReducer";
import { update as updateAuthUser } from "@/slices/auth/authReducer";
import { setTheme as setThemeAction } from "@/slices/changeTheme/changeThemeReducer";
import { addNewMessages, clearMessage } from "@/slices/firebase/firebaseReducer";
import { store } from "@/store";
import { rootReducerName } from "@/store/rootReducer";
import type { PageChatStructuredSetupPayload } from "@/types/pagechat";
import { coerceStructuredSetupPayload } from "@/types/pagechat";
import { buildDashboardContextKey } from "@/utils/chatAskCaddyHelpers";
import { isTenantAdmin as isTenantAdminUtil } from "@/utils/permissions";
import { toJSDate, toJSDateToLocalString } from "@/utils/timeUTC";
import { EventSourcePolyfill, NativeEventSource } from "event-source-polyfill";
import lz from "lzutf8";
import { useRouter } from "next/router";
import PropTypes from "prop-types";
import Session from "supertokens-auth-react/recipe/session";

const normalizeEventsourceUrl = (eventsourceUrl?: string | null, taskId?: string): string => {
  const trimmedUrl = typeof eventsourceUrl === "string" ? eventsourceUrl.trim() : "";
  if (trimmedUrl) {
    if (/^https?:\/\//i.test(trimmedUrl)) {
      try {
        const parsedUrl = new URL(trimmedUrl);
        return canonicalizeSessionmgrPublicPath(`${parsedUrl.pathname}${parsedUrl.search}`);
      } catch {
        return canonicalizeSessionmgrPublicPath(trimmedUrl);
      }
    }
    return canonicalizeSessionmgrPublicPath(trimmedUrl);
  }

  if (taskId) {
    return sessionmgrPublicPath(`/sess/v1/chat/page/sse/${taskId}`);
  }

  return "";
};

type ManagedEventSource = {
  close: () => void;
  readyState: number;
  onerror: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent<string>) => void) | null;
};

interface ChatAIState {
  notification: any;
  message: any;
}
export const loadingMessageId = "loadingMessageId";
type ChatData = {
  message: string;
  files?: (string | ArrayBuffer)[];
  contextPannels?: unknown;
};

export type PageChatResponse = FetchRawResponse | followingChatResponse | sessionmgrTaskResponse;

export type ChatOnPageOptions = {
  additionalData?: Record<string, unknown>;
  /** Page resource identity; kept separate from the legacy conversation/task id argument. */
  pageSourceId?: string | null;
  /** Explicit PageChat task to continue; null starts a new task even if global chat has another task active. */
  taskId?: string | null;
  onResponse?: (response: PageChatResponse) => void;
  onStructuredSetup?: (setup: PageChatStructuredSetupPayload) => void;
};

type PendingPageChatLaunch = {
  additionalData: Record<string, unknown>;
  pageSrc: string;
  pageSourceId?: string | null;
  taskId?: string | null;
  onResponse?: (response: PageChatResponse) => void;
  onStructuredSetup?: (setup: PageChatStructuredSetupPayload) => void;
};

interface ChatAIContextValue extends ChatAIState {
  isShowChatBoxTask: boolean;
  showChatbox: boolean;
  openChatbox: () => void;
  closeChatbox: () => void;
  refreshPage: boolean;
  setRefreshPage: React.Dispatch<React.SetStateAction<boolean>>;
  disabledChatbox: boolean;
  setDisabledChatbox: React.Dispatch<React.SetStateAction<boolean>>;
  loadingChatbox: boolean;
  setLoadingChatbox: React.Dispatch<React.SetStateAction<boolean>>;
  titleChatbox: string;
  setTitleChatbox: React.Dispatch<React.SetStateAction<string>>;
  taskID: string;
  setTaskID: React.Dispatch<React.SetStateAction<string>>;
  taskDescription: string;
  setTaskDescription: React.Dispatch<React.SetStateAction<string>>;
  taskSource: string;
  setTaskSource: React.Dispatch<React.SetStateAction<string>>;
  assistantProfileDisclosure: AssistantProfileDisclosureModel;
  assistantProfileSelection: AssistantProfileSelection;
  assistantProfileSessionKey: number;
  applyAssistantProfileSelection: (selection: AssistantProfileSelection) => void;
  rememberAssistantProfileSelection: (selection: AssistantProfileSelection) => void;
  handleRefreshChatbox: (state: TaskStatusType) => void;
  handleEventSourceURL: (url: string) => void;
  generateMessage: (item: { id?: string; sentFrom: "BOT" | "USER"; content: string; loading?: boolean }) => void;
  previewModalOpen: boolean;
  setPreviewModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  previewData: string;
  setPreviewData: React.Dispatch<React.SetStateAction<string>>;
  handleDisconnectEventSource: () => void;
  needGuide: boolean;
  setNeedGuide: React.Dispatch<React.SetStateAction<boolean>>;
  codeModalOpen: boolean;
  setCodeModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  codeData: CodeModalProps;
  setCodeReadonly: React.Dispatch<React.SetStateAction<boolean>>;
  codeReadonly: boolean;
  setCodeData: React.Dispatch<React.SetStateAction<CodeModalProps>>;
  connectState: number;
  setConnectState: React.Dispatch<React.SetStateAction<number>>;
  statusRevision: number;
  chatOnPage: (
    value: ChatData,
    page_src: string,
    pagesource_id?: string | null,
    options?: ChatOnPageOptions
  ) => Promise<void>;
  preparePageChatLaunch: (page_src: string, options?: ChatOnPageOptions) => void;
  refScreenCapture: any;
  contextPannels: Object;
  setContextPannels: React.Dispatch<React.SetStateAction<Object>>;
  isShowChatHistory: boolean;
  setIsShowChatHistory: React.Dispatch<React.SetStateAction<boolean>>;
  handleConnectSSE: (
    res: PageChatResponse,
    options?: Pick<ChatOnPageOptions, "onResponse" | "onStructuredSetup">
  ) => void;
  fetchChatHistory: () => void;
  chatHistory: any[];
  showPersonalizationOverlay: boolean;
  setShowPersonalizationOverlay: React.Dispatch<React.SetStateAction<boolean>>;
  isTenantAdmin: boolean;
  isOnboardingPending: boolean;
  onboardingStatus: string;
  tenantOnboardingStatusLoaded: boolean;
}

const initialChatAIState: ChatAIState = {
  notification: null,
  message: null
};

const inintialCodeData: CodeModalProps = {
  data: {
    files: [],
    plan_step_id: "",
    task_id: ""
  },
  action_url: ""
};

export const ChatAIContext = createContext<ChatAIContextValue>({
  ...initialChatAIState,
  isShowChatBoxTask: true,
  showChatbox: false,
  openChatbox: () => {},
  closeChatbox: () => {},
  refreshPage: false,
  setRefreshPage: () => {},
  disabledChatbox: false,
  setDisabledChatbox: () => {},
  loadingChatbox: false,
  setLoadingChatbox: () => {},
  titleChatbox: "New Chat",
  setTitleChatbox: () => {},
  taskID: "",
  setTaskID: () => {},
  taskDescription: "",
  setTaskDescription: () => {},
  taskSource: "",
  setTaskSource: () => {},
  assistantProfileDisclosure: parseAssistantProfileDisclosure(null),
  assistantProfileSelection: autoAssistantProfileSelection(),
  assistantProfileSessionKey: 0,
  applyAssistantProfileSelection: () => {},
  rememberAssistantProfileSelection: () => {},
  handleRefreshChatbox: () => {},
  handleEventSourceURL: () => {},
  generateMessage: () => {},
  previewModalOpen: false,
  setPreviewModalOpen: () => {},
  previewData: "",
  setPreviewData: () => {},
  handleDisconnectEventSource: () => {},
  needGuide: false,
  setNeedGuide: () => {},
  codeModalOpen: false,
  setCodeModalOpen: () => {},
  codeReadonly: false,
  setCodeReadonly: () => {},
  codeData: inintialCodeData,
  setCodeData: () => {},
  connectState: 0,
  setConnectState: () => {},
  statusRevision: 0,
  chatOnPage: async () => {},
  preparePageChatLaunch: () => {},
  refScreenCapture: null,
  contextPannels: {},
  setContextPannels: () => {},
  isShowChatHistory: false,
  setIsShowChatHistory: () => {},
  handleConnectSSE: () => {},
  fetchChatHistory: () => {},
  chatHistory: [],
  showPersonalizationOverlay: false,
  setShowPersonalizationOverlay: () => {},
  isTenantAdmin: false,
  isOnboardingPending: false,
  onboardingStatus: "",
  tenantOnboardingStatusLoaded: false
});

interface ChatAIProviderProps {
  children: ReactNode;
}

export const ChatAIProvider: FC<ChatAIProviderProps> = (props) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { children } = props;
  const dashboard = useDashboard();
  const [showChatbox, setShowChatbox] = useState<boolean>(false);
  const [isShowChatBoxTask, setIsShowChatBoxTask] = useState<boolean>(true);
  const [disabledChatbox, setDisabledChatbox] = useState<boolean>(false);
  const [loadingChatbox, setLoadingChatbox] = useState<boolean>(false);
  const [refreshPage, setRefreshPage] = useState<boolean>(false);
  const [titleChatbox, setTitleChatbox] = useState<string>("New Chat");
  const [taskID, setTaskID] = useState<string>("");
  const [taskDescription, setTaskDescription] = useState<string>("");
  const [taskSource, setTaskSource] = useState<string>("");
  const [assistantProfileDisclosure, setAssistantProfileDisclosure] = useState<AssistantProfileDisclosureModel>(() =>
    parseAssistantProfileDisclosure(null)
  );
  const [assistantProfileSelection, setAssistantProfileSelection] = useState<AssistantProfileSelection>(() =>
    readRememberedAssistantProfileSelection()
  );
  const [assistantProfileSessionKey, setAssistantProfileSessionKey] = useState(0);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<string>("");
  const [pendingPageChatLaunch, setPendingPageChatLaunch] = useState<PendingPageChatLaunch | null>(null);
  const [needGuide, setNeedGuide] = useState<boolean>(true);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [codeReadonly, setCodeReadonly] = useState(false);
  const [codeData, setCodeData] = useState<CodeModalProps>(null);
  const [connectState, setConnectState] = useState<number>(0);
  const refScreenCapture: any = useRef(null);
  const [chatHistory, setChatHistory] = useState([]);

  const [contextPannels, setContextPannels] = useState({});
  const [isShowChatHistory, setIsShowChatHistory] = useState(false);
  const [showPersonalizationOverlay, setShowPersonalizationOverlay] = useState<boolean>(false);
  const [tenantOnboardingStatus, setTenantOnboardingStatus] = useState<string | null>(null);
  const [tenantOnboardingStatusLoaded, setTenantOnboardingStatusLoaded] = useState<boolean>(false);
  const [sessionTenantGuid, setSessionTenantGuid] = useState<string | null>(null);

  // Auto-show checklist when status is explicitly PENDING or if it's currently unset
  const user = useSelector((state: any) => state[rootReducerName.auth]?.user);
  const authRole = useSelector((state: any) => state[rootReducerName.auth]?.role);
  const isTenantAdmin = isTenantAdminUtil(user) || authRole === 0;
  const rawStatus = user?.onboardingStatus || user?.onboarding_status;
  const onboardingStatusFromAuth = (rawStatus || "PENDING").toString().toUpperCase();
  const isTenantSessionVerified = Boolean(
    user?.tenantGuid && sessionTenantGuid && user.tenantGuid === sessionTenantGuid
  );

  useEffect(() => {
    let cancelled = false;

    if (!user?.tenantGuid) {
      setSessionTenantGuid(null);
      return () => {
        cancelled = true;
      };
    }

    ensureSuperTokensInitialized();
    void Session.getAccessTokenPayloadSecurely()
      .then((payload) => {
        if (cancelled) return;
        const tenantGuid = String(payload?.spTGuid || "");
        setSessionTenantGuid(tenantGuid || null);
      })
      .catch((err) => {
        if (!cancelled) {
          logger.warn("ChatAIContext: failed to read secure session payload", { err });
          setSessionTenantGuid(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [user?.tenantGuid]);

  // Prefer tenant profile (backend) onboarding_status when available.
  // This avoids relying on JWT custom claim drift (e.g. tenant status updated but token claim not refreshed).
  const onboardingStatus = (tenantOnboardingStatus || onboardingStatusFromAuth || "PENDING").toString().toUpperCase();
  // Treat onboarding status as the source of truth.
  // Using `firstLogin` here causes the activation plan to re-appear even when a tenant is marked SKIPPED.
  // Activation onboarding mutates tenant-wide settings; keep it admin-only.
  const isOnboardingPending = isTenantAdmin && onboardingStatus === "PENDING";

  // Sync tenant-scoped profile fields (onboarding_status/industry) from backend so they persist across sessions.
  useEffect(() => {
    if (!user?.tenantGuid || !isTenantSessionVerified) {
      setTenantOnboardingStatus(null);
      setTenantOnboardingStatusLoaded(false);
      return;
    }
    let cancelled = false;
    setTenantOnboardingStatusLoaded(false);

    getManagementTenantInfo(user.tenantGuid)
      .then((res: any) => {
        if (cancelled) return;
        const content = res?.content || res;
        const nextStatusRaw = content?.onboarding_status;
        const nextStatus = nextStatusRaw ? String(nextStatusRaw).toUpperCase() : null;
        setTenantOnboardingStatus(nextStatus);
        setTenantOnboardingStatusLoaded(true);

        const nextIndustry = content?.industry;
        const nextReportTimeZone = content?.report_timezone;
        const nextTenantConfig = content?.tenant_config;

        const shouldUpdateTenantConfig = (() => {
          if (!nextTenantConfig || typeof nextTenantConfig !== "object") return false;
          const curr = (user as any)?.tenant_config;
          if (!curr || typeof curr !== "object") return true;
          try {
            return JSON.stringify(curr) !== JSON.stringify(nextTenantConfig);
          } catch {
            return true;
          }
        })();

        const shouldUpdateUser =
          (nextStatus &&
            nextStatus !== String(user?.onboardingStatus || user?.onboarding_status || "").toUpperCase()) ||
          (nextIndustry && nextIndustry !== user?.industry) ||
          (nextReportTimeZone && nextReportTimeZone !== user?.report_timezone) ||
          shouldUpdateTenantConfig;

        if (shouldUpdateUser) {
          dispatch(
            updateAuthUser({
              user: {
                ...user,
                ...(nextStatus
                  ? {
                      onboardingStatus: nextStatus,
                      onboarding_status: nextStatus
                    }
                  : {}),
                ...(nextIndustry ? { industry: nextIndustry } : {}),
                ...(nextReportTimeZone ? { report_timezone: nextReportTimeZone } : {}),
                ...(shouldUpdateTenantConfig ? { tenant_config: nextTenantConfig } : {})
              }
            })
          );
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        logger.error("Failed to fetch tenant management info for onboarding", { err });
        setTenantOnboardingStatus(null);
        // Fail open to auth-claim fallback rather than permanently hiding onboarding UI.
        setTenantOnboardingStatusLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [dispatch, isTenantSessionVerified, user?.tenantGuid]);

  useEffect(() => {
    const setupRouteActive = isSignupSetupRoute(router.asPath || router.pathname);

    if (setupRouteActive) {
      setShowPersonalizationOverlay(false);
      return;
    }

    // Avoid showing onboarding UI while the tenant-scoped onboarding_status is still loading.
    // This prevents a brief flash when auth claim defaults to PENDING.
    if (user?.tenantGuid && (!isTenantSessionVerified || !tenantOnboardingStatusLoaded)) {
      setShowPersonalizationOverlay(false);
      return;
    }

    if (isOnboardingPending) {
      // Client-side guardrails for the personalization overlay.
      // - sessionStorage: hide for the current session when user clicks "I'll do this later" or closes.
      // - localStorage: persistently hide once the user has completed the "Tailor to your business" step.
      const isDismissed =
        typeof window !== "undefined" && sessionStorage.getItem("onboarding_overlay_dismissed") === "true";
      const completedKey = user?.tenantGuid
        ? `onboarding_overlay_completed:${user.tenantGuid}`
        : "onboarding_overlay_completed";
      const isCompletedLocal = typeof window !== "undefined" && localStorage.getItem(completedKey) === "true";

      // Also respect a tenant-scoped flag so completion is shared across devices.
      const tenantConfig = (user as any)?.tenant_config || null;
      const isCompletedRemote = Boolean(
        tenantConfig && typeof tenantConfig === "object" && tenantConfig.personalization_overlay_completed === true
      );

      const isCompleted = isCompletedLocal || isCompletedRemote;

      // Only auto-show overlay if industry isn't set yet AND the user hasn't already completed it.
      if (!isDismissed && !isCompleted && !user?.industry) {
        setShowPersonalizationOverlay(true);
      } else {
        setShowPersonalizationOverlay(false);
      }
    } else {
      setShowPersonalizationOverlay(false);
    }
  }, [
    isOnboardingPending,
    tenantOnboardingStatusLoaded,
    isTenantSessionVerified,
    user?.industry,
    user?.tenantGuid,
    router.asPath,
    router.pathname,
    (user as any)?.tenant_config
  ]);

  // Hydrate global UI settings (Theme/Language) from user preferences
  useEffect(() => {
    if (!user?.id || !user?.preferences) return;

    // 1. Hydrate Theme
    const preferredThemeId = user.preferences.theme_id;
    const currentThemeRedux = store.getState()[rootReducerName.changeTheme].curTheme;
    if (preferredThemeId && preferredThemeId !== currentThemeRedux) {
      dispatch(setThemeAction(preferredThemeId));
    }

    // 2. Hydrate Language
    const preferredLang = user.preferences.language;
    if (preferredLang && preferredLang !== router.locale) {
      // Avoid redirecting on initial render if already matching
      // Also avoid loops
      const { pathname, asPath, query } = router;
      router.push({ pathname, query }, asPath, { locale: preferredLang });
    }
  }, [user?.id, user?.preferences?.theme_id, user?.preferences?.language]);

  ///
  //Code block for function refresh state chatbox
  ///
  // logic:
  // step 1: setState{last_message_timestamp,task_id,user_input_allowed}
  // set state last_message_timestamp, TaskId, user_input_allowed
  // when taskId change run step2
  // step2: get taskStatus : response :{last_message_timestamp,task_id,user_input_allowed}
  // if user_input_allowed = false set message IN_PROGRESS, disabled button and chatbox => replay step2 after any second
  // if user_input_allowed = True check next condition:
  // if (status[last_message_timestamp] > state[last_message_timestamp])
  // get chat bubbles : response {count,chat_bubbles,...}
  // if count <= 1 || last(chat_bubbles).created_at != status[last_message_timestamp] replay step2 after any second
  // else set chatbox content = chat_bubbles, set message COMPLETED, enable button and chat box,
  // end
  // lastUpdate state

  const resetTime = 5; // time replay call get status task
  const limitTime = 30; // time replay call get status task
  const [lastUpdateState, setLastUpdateState] = useState<TaskStatusType>();
  const [countTime, setCountTime] = useState<number>(0);
  const [limitCount, setLimitCount] = useState<number>(0);
  const countDownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const eventSourceRef = useRef<ManagedEventSource | null>(null);
  const responseCallbackRef = useRef<ChatOnPageOptions["onResponse"]>(null);
  const structuredSetupCallbackRef = useRef<ChatOnPageOptions["onStructuredSetup"]>(null);
  const [statusRevision, setStatusRevision] = useState<number>(0);

  const fetchChatHistory = async () => {
    // Scope dashboard chat history to the current dashboard context so the history drawer
    // doesn't mix unrelated dashboard threads.
    const dashboard_context_key = buildDashboardContextKey({
      route: router.pathname,
      targetId: dashboard?.targetId,
      targetType: dashboard?.targetType,
      teamOnly: dashboard?.teamOnly,
      pipelineId: dashboard?.pipelineId
    });
    const chatHistoryResponse: any = await getTaskList(dashboard_context_key ? { dashboard_context_key } : undefined);
    setChatHistory(chatHistoryResponse.content.chat_rooms);
  };

  const closeEventSource = () => {
    const currentEventSource = eventSourceRef.current;
    if (!currentEventSource) return;

    currentEventSource.close();
    setConnectState(currentEventSource.readyState);
    if (eventSourceRef.current === currentEventSource) {
      eventSourceRef.current = null;
    }
  };

  const notifyStructuredSetup = (
    value: unknown,
    callback: ChatOnPageOptions["onStructuredSetup"] = structuredSetupCallbackRef.current
  ) => {
    const record = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, any>) : null;
    const sender = String(record?.sender || record?.sender_type || "").toUpperCase();
    if (sender && sender !== "BOT" && sender !== "ASSISTANT") return;

    const setup = coerceStructuredSetupPayload(record?.additional_data) || coerceStructuredSetupPayload(record);
    if (!setup || !callback) return;
    try {
      callback(setup);
    } catch (error) {
      logger.error("ChatAIContext: structured setup callback failed", error);
    }
  };

  const notifyStructuredSetups = (
    value: unknown,
    callback: ChatOnPageOptions["onStructuredSetup"] = structuredSetupCallbackRef.current
  ) => {
    const record = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, any>) : null;
    const history = record?.chat_history;
    if (Array.isArray(history)) {
      history.forEach((chat) => notifyStructuredSetup(chat, callback));
    } else if (history && typeof history === "object") {
      notifyStructuredSetup(history, callback);
    }
    notifyStructuredSetup(record, callback);
  };

  const captureAssistantProfileDisclosure = (value: unknown) => {
    const envelope = findAssistantProfileDisclosure(value);
    if (envelope.found) {
      setAssistantProfileDisclosure(parseAssistantProfileDisclosure(envelope.value));
    }
  };

  const createEventSource = (eventSource_URL: string) => {
    if (!eventSource_URL) return null;

    closeEventSource();

    const EventSource = EventSourcePolyfill || NativeEventSource;
    const eventSource = new EventSource(joinServiceUrl(SESSIONMGR_URL, eventSource_URL), {
      withCredentials: true
    }) as ManagedEventSource; // /sess/v1/chat/tasks/sse/[task_id]
    eventSourceRef.current = eventSource;
    setConnectState(eventSource.readyState);

    eventSource.onerror = (err) => {
      logger.error(`EventSource failed url=${eventSource_URL} :`, err);
      if (eventSourceRef.current === eventSource) {
        closeEventSource();
      }
    };
    eventSource.onmessage = function (event) {
      if (eventSourceRef.current !== eventSource) return;
      setConnectState(eventSource.readyState);
      if (typeof event.data !== "string" || !event.data) return;

      let newMessage: any;
      try {
        newMessage = JSON.parse(event.data);
      } catch (error) {
        logger.error("Failed to parse JSON data:", error);
        return;
      }
      captureAssistantProfileDisclosure(newMessage);
      notifyStructuredSetups(newMessage);
      if (newMessage.shouldClose) {
        closeEventSource();
        return;
      }
      // Update the state to include the new message
      if (newMessage["status"]) {
        setStatusRevision((currentRevision) => currentRevision + 1);
        handleRefreshChatbox({
          task_id: newMessage.task_id,
          user_input_allowed: true
        });
      }
      if (newMessage["chat_history"]) {
        const chatHistory: any = newMessage["chat_history"];
        const msgText = (() => {
          if (typeof chatHistory.message === "string") return chatHistory.message;
          const contentEnvelope = chatHistory.content;
          if (contentEnvelope && typeof contentEnvelope === "object") {
            const inner = (contentEnvelope as any).content;
            if (inner && typeof inner === "object" && typeof (inner as any).text === "string")
              return (inner as any).text;
          }
          return "";
        })();

        const sender = (() => {
          if (typeof chatHistory.sender === "string") return chatHistory.sender;
          const st = String(chatHistory.sender_type || "").toLowerCase();
          if (st === "assistant" || st === "bot") return "BOT";
          return "USER";
        })();

        const newChatHistory: MessageModel[] = [
          {
            content: msgText,
            createdAt: toJSDate(chatHistory.received_at),
            id: String(chatHistory.id),
            sentFrom: sender,
            sentTo: sender == "BOT" ? "USER" : "BOT",
            loading: false,
            transient: chatHistory.additional_data ? chatHistory.additional_data.transient : undefined,
            context_menu: chatHistory.additional_data ? chatHistory.additional_data.context_menu : undefined,
            quick_replies: chatHistory.additional_data ? chatHistory.additional_data.quick_replies : undefined,
            additional_data: chatHistory.additional_data ? { ...chatHistory.additional_data } : {}
          }
        ];
        dispatch(addNewMessages({ message: newChatHistory })); //push message to store
        if (
          chatHistory["page_src"] == "PAGES_GROUP_PAGE" &&
          chatHistory["additional_data"] &&
          chatHistory["additional_data"]["result"]
        ) {
          // setPreviewData(chatHistory["additional_data"]["result"]); //TODO: load result string
          const base64Page = lz.encodeBase64(lz.compress(chatHistory["additional_data"]["result"]));
          setPreviewData(base64Page);
        }
      }
    };

    return eventSource;
  };

  // Cleanup EventSource on provider unmount
  useEffect(() => {
    return () => {
      closeEventSource();
    };
  }, []);

  const handleEventSourceURL = (url: string) => {
    if (!url) {
      closeEventSource();
      return;
    }

    createEventSource(normalizeEventsourceUrl(url));
  };

  const handleDisconnectEventSource = () => {
    closeEventSource();
  };

  const handleRefreshChatbox = (state: TaskStatusType) => {
    setLastUpdateState(state);
  };

  useEffect(() => {
    // step2
    if (lastUpdateState?.task_id) {
      setLimitCount(0);
      // checkStatusMess();
    }
    setDisabledChatbox((lastUpdateState && !lastUpdateState?.user_input_allowed) || false);
  }, [lastUpdateState]);

  useEffect(() => {
    // step 2 when user_input_allowed = false
    if (!lastUpdateState) return;

    // Clear any existing timeout
    if (countDownTimeoutRef.current) {
      clearTimeout(countDownTimeoutRef.current);
      countDownTimeoutRef.current = null;
    }

    // Stop if there are no new tasks yet
    if (countTime === 0) {
      if (limitCount * resetTime < limitTime) {
        setLimitCount(limitCount + 1);
        // count down limit, check status
        // checkStatusMess();
      } else {
        setDisabledChatbox(false);
      }
    } else {
      // count down with proper cleanup
      countDownTimeoutRef.current = setTimeout(() => {
        setCountTime((prev) => prev - 1);
      }, 1000);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (countDownTimeoutRef.current) {
        clearTimeout(countDownTimeoutRef.current);
        countDownTimeoutRef.current = null;
      }
    };
  }, [countTime, lastUpdateState, limitCount]);

  const generateMessage = (item: { id?: string; sentFrom: "BOT" | "USER"; content: string; loading?: boolean }) => {
    const itemId = item.id || new Date(Date.now()).toLocaleDateString();
    const newMessage = [
      {
        id: itemId,
        sentFrom: item.sentFrom, //"USER",
        sentTo: "",
        content: item.content,
        createdAt: new Date(Date.now()),
        loading: item.loading || false
      }
    ];

    dispatch(addNewMessages({ message: newMessage })); //push message to store
  };

  const applyAssistantProfileSelection = (selection: AssistantProfileSelection) => {
    setAssistantProfileSelection(selection);
  };

  const rememberAssistantProfileSelection = (selection: AssistantProfileSelection) => {
    setAssistantProfileSelection(selection);
    persistRememberedAssistantProfileSelection(selection);
  };

  ///
  //Code block for function refresh state chatbox
  ///
  const openChatbox = () => {
    setShowChatbox(true);
    setIsShowChatBoxTask(true);
  };

  const closeChatbox = () => {
    setShowChatbox(false);
    setIsShowChatBoxTask(false);
    setPendingPageChatLaunch(null);
    responseCallbackRef.current = null;
    structuredSetupCallbackRef.current = null;
  };

  const preparePageChatLaunch = (page_src: string, options?: ChatOnPageOptions) => {
    const additionalData = options?.additionalData ? { ...options.additionalData } : {};

    dispatch(clearMessage());
    dispatch(pushParams({ taskId: null }));
    setTitleChatbox("New Chat");
    setTaskDescription("");
    setTaskID("");
    setConnectState(0);
    setDisabledChatbox(false);
    setTaskSource(page_src);
    setAssistantProfileDisclosure(parseAssistantProfileDisclosure(null));
    setAssistantProfileSelection(readRememberedAssistantProfileSelection());
    setAssistantProfileSessionKey((currentKey) => currentKey + 1);
    setPendingPageChatLaunch({
      additionalData,
      pageSrc: page_src,
      pageSourceId: options?.pageSourceId,
      taskId: options?.taskId,
      onResponse: options?.onResponse,
      onStructuredSetup: options?.onStructuredSetup
    });
    responseCallbackRef.current = null;
    structuredSetupCallbackRef.current = null;
    openChatbox();
  };

  useEffect(() => {
    // push to store pageId current
    if (router.route.includes("[id]"))
      store.dispatch(pushPageId("/" + router.route.split("/")[1])); // e.g) /tasks/[id] => tasks
    else store.dispatch(pushPageId(router.route));
  }, [router.route]);

  useEffect(() => {
    // const handleClick = (_: MouseEvent) => {};

    //handle save object copied to store
    const handleCopyAnywhere = () => {
      const selectedText = window.getSelection().toString();
      store.dispatch(pushCopiedText(selectedText));
    };

    window.addEventListener("copy", handleCopyAnywhere); // add event copy => handleCopyAnywhere
    // window.addEventListener('click', handleClick);
    return () => {
      // window.removeEventListener('click', handleClick);
      window.removeEventListener("copy", handleCopyAnywhere);
    };
  }, []);
  const aiInfor: AIState = useSelector((state) => state[rootReducerName.ai]);
  const firebase = useSelector((state) => state[rootReducerName.firebase]);

  const handleConnectSSE = (
    res: PageChatResponse,
    options?: Pick<ChatOnPageOptions, "onResponse" | "onStructuredSetup">
  ) => {
    setLoadingChatbox(true);
    dispatch(clearMessage()); // clear chatbox

    // If res has a 'content' property, use it; otherwise, treat res as the content
    const content = typeof res === "object" && res !== null && "content" in res ? (res as any).content : res;
    const responseCallback = options?.onResponse || responseCallbackRef.current;
    if (responseCallback) {
      try {
        responseCallback(res);
      } catch (error) {
        logger.error("ChatAIContext: PageChat response callback failed", error);
      }
    }
    notifyStructuredSetups(content, options?.onStructuredSetup || structuredSetupCallbackRef.current);

    // Type guard: check if content is followingChatResponse inner content
    if (
      content &&
      typeof content === "object" &&
      "task_id" in content &&
      "user_input_allowed" in content &&
      "chat_history" in content
    ) {
      const chatContent = content as any;
      captureAssistantProfileDisclosure(chatContent);
      const state: TaskStatusType = {
        // last_message_timestamp: Number(chatContent.last_message_timestamp),
        task_id: chatContent.task_id,
        user_input_allowed: chatContent.user_input_allowed
      };
      handleRefreshChatbox(state);
      dispatch(pushParams({ taskId: chatContent.task_id })); // , pagesource_id: chatContent.pagesource_id
      // setTitleChatbox(chatContent.chat_history[0].message);
      setTaskID(chatContent.task_id);

      const newMessage = chatContent.chat_history.map((chat: any) => {
        return {
          id: toJSDateToLocalString(chat.received_at),
          sentFrom: chat.sender,
          sentTo: "",
          content: chat.message,
          createdAt: toJSDate(chat.received_at),
          transient: chat.additional_data ? chat.additional_data.transient : undefined,
          context_menu: chat.additional_data ? chat.additional_data.context_menu : undefined,
          quick_replies: chat.additional_data ? chat.additional_data.quick_replies : undefined,
          additional_data: chat.additional_data ? { ...chat.additional_data } : {}
        };
      });
      dispatch(addNewMessages({ message: newMessage })); //push message to store

      handleEventSourceURL(normalizeEventsourceUrl(chatContent.eventsource_url, chatContent.task_id));
    } else {
      // Handle sessionmgrTaskResponse or unknown type
      logger.info("handleConnectSSE: received sessionmgrTaskResponse or unknown type", content);
      // Optionally, handle differently or skip
    }
  };

  const chatOnPage = async (
    value: ChatData,
    page_src: string,
    pagesource_id?: string | null,
    options?: ChatOnPageOptions
  ) => {
    const assistantPageSlug = resolveAssistantPageSlugByPath(router.pathname);
    const pageSlug = assistantPageSlug || undefined;
    const dashboard_context_key = buildDashboardContextKey({
      route: router.pathname,
      targetId: dashboard?.targetId,
      targetType: dashboard?.targetType,
      teamOnly: dashboard?.teamOnly,
      pipelineId: dashboard?.pipelineId
    });
    setTaskSource(page_src);
    const pendingAdditionalData =
      pendingPageChatLaunch && pendingPageChatLaunch.pageSrc === page_src
        ? pendingPageChatLaunch.additionalData
        : undefined;
    const pendingLaunch =
      pendingPageChatLaunch && pendingPageChatLaunch.pageSrc === page_src ? pendingPageChatLaunch : null;
    const pageSourceId = options?.pageSourceId ?? pendingLaunch?.pageSourceId ?? null;

    const additional_data: Record<string, unknown> = {
      is_auto_execution: firebase.additional_data?.is_auto_execution || false,
      files: value.files || [],
      context: value.contextPannels
    };
    if (pendingAdditionalData) {
      Object.assign(additional_data, pendingAdditionalData);
    }
    if (options?.additionalData) {
      Object.assign(additional_data, options.additionalData);
    }
    if (pageSlug) {
      additional_data.page_slug = pageSlug;
    }
    if (assistantPageSlug) {
      additional_data.assistant_page_slug = assistantPageSlug;
    }
    if (pageSourceId) {
      additional_data.page_source_id = pageSourceId;
    }
    // This is a user preference hint only. The server must re-authorize every
    // profile, scope, and route before using it; it is never client authority.
    additional_data.assistant_profile_selection_hint = toAssistantProfileSelectionHint(assistantProfileSelection);
    setPreviewModalOpen(true); //TODO: review
    const body = {
      sender: "USER",
      message: value.message,
      additional_data: additional_data,
      created_at: new Date(Date.now()).getTime(),
      plan_type: firebase.plan_type || "INTERACTIVE",
      page_src: page_src.toUpperCase(),
      // PageChat/sessionmgr use pagesrc_id as the task identifier and expect a UUID.
      // Page resource identity travels in additional_data instead.
      pagesrc_id: null,
      parent_id: null,
      marketplace: 0,
      // Used by backend to group pagechat threads per dashboard context.
      dashboard_context_key: page_src === "dashboard" ? dashboard_context_key : undefined
      // ...value
    };
    const activeTaskId =
      typeof (taskID || aiInfor.params?.taskId) === "string" ? String(taskID || aiInfor.params?.taskId).trim() : "";
    const requestedTaskId = options?.taskId !== undefined ? options.taskId : pendingLaunch?.taskId;
    const hasRequestedTaskId = options?.taskId !== undefined || pendingLaunch?.taskId !== undefined;
    const existingTaskId = hasRequestedTaskId
      ? String(requestedTaskId || "").trim()
      : activeTaskId || (pendingAdditionalData ? "" : pagesource_id || "");
    const structuredSetupCallback = options?.onStructuredSetup || pendingLaunch?.onStructuredSetup;
    const responseCallback = options?.onResponse || pendingLaunch?.onResponse;
    if (structuredSetupCallback || !existingTaskId) {
      structuredSetupCallbackRef.current = structuredSetupCallback || null;
    }
    if (responseCallback || !existingTaskId) {
      responseCallbackRef.current = responseCallback || null;
    }
    const clearPendingLaunchIfUsed = () => {
      if (pendingAdditionalData) {
        setPendingPageChatLaunch(null);
      }
    };

    if (existingTaskId) {
      followingPageChat({
        body,
        id: existingTaskId
      })
        .then((res) => {
          clearPendingLaunchIfUsed();
          return handleConnectSSE(res);
        })
        .catch((err) => {
          logger.error("Error connecting to SSE (existing chat):", err);
        })
        .finally(() => {
          setLoadingChatbox(false);
        });
    } else {
      newPageChat({ body })
        .then((res) => {
          clearPendingLaunchIfUsed();
          setTitleChatbox(res.content.chat_history[0].message);
          return handleConnectSSE(res);
        })
        .catch((err) => {
          logger.error("Error connecting to SSE (new chat):", err);
        })
        .finally(() => {
          setLoadingChatbox(false);
        });
    }
    return Promise.resolve();
  };

  return (
    <ChatAIContext.Provider
      value={{
        ...initialChatAIState,
        isShowChatBoxTask,
        showChatbox,
        openChatbox,
        closeChatbox,
        refreshPage,
        setRefreshPage,
        disabledChatbox,
        setDisabledChatbox,
        loadingChatbox,
        setLoadingChatbox,
        titleChatbox,
        setTitleChatbox,
        taskID,
        setTaskID,
        taskDescription,
        setTaskDescription,
        taskSource,
        setTaskSource,
        assistantProfileDisclosure,
        assistantProfileSelection,
        assistantProfileSessionKey,
        applyAssistantProfileSelection,
        rememberAssistantProfileSelection,
        handleRefreshChatbox,
        handleEventSourceURL,
        generateMessage,
        previewModalOpen,
        setPreviewModalOpen,
        previewData,
        setPreviewData,
        handleDisconnectEventSource,
        needGuide,
        setNeedGuide,
        codeModalOpen,
        setCodeModalOpen,
        codeData,
        setCodeReadonly,
        codeReadonly,
        setCodeData,
        connectState,
        setConnectState,
        statusRevision,
        chatOnPage,
        preparePageChatLaunch,
        refScreenCapture,
        contextPannels,
        setContextPannels,
        isShowChatHistory,
        setIsShowChatHistory,
        handleConnectSSE,
        fetchChatHistory,
        chatHistory,
        showPersonalizationOverlay,
        setShowPersonalizationOverlay,
        isTenantAdmin,
        isOnboardingPending,
        onboardingStatus,
        tenantOnboardingStatusLoaded
      }}
    >
      {children}
    </ChatAIContext.Provider>
  );
};

ChatAIProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const ChatAIConsumer = ChatAIContext.Consumer;
